import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchBar } from '@/components/common/SearchBar';
import { EchoCard } from '@/components/common/EchoCard';
import { FilterPopup } from '@/components/common/FilterPopup';
import { PageHeader } from '@/components/layout/PageHeader';
import type { EchoKey } from '@/utils/constants';
import type { ActiveFilters, FilterSectionId } from '@/types/filters';
import type { Echo } from '@/types';
import { useChatContext } from '@/components/layout/ChatLayoutContext';
import noEchoImage from '@/assets/images/echo/no_echo.webp';
import echoCreatedImage from '@/assets/images/webp/echo_created.webp';
import filterIcon from '@/assets/images/svg/filter_icon.svg';
import waveContactListSvg from '@/assets/images/svg/wave_element_contact_list.svg';
import clsx from 'clsx';
import { useEchoStore } from '@/context/EchoContext';
import { SmartEchoCard } from '../components/SmartEchoCard';
import { FILTER_SECTIONS } from '@/utils/filterConstants';
import {
  getUniqueDomains,
  getEchoDomain,
  buildEchoSlugMap,
  sortEchoesByPopularity,
  POPULARITY_LABELS,
} from '@/utils/echoUtils';
import type { PopularityFilter } from '@/utils/echoUtils';
import { useTrendingEchoIds } from '@/hooks/useTrendingEchoIds';
import { HeaderCredits } from '@/components/common/HeaderCredits';
import { analytics } from '@/utils/analytics';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useAllEchoStats, BatchedEchoStatsProvider } from '@/hooks/useAllEchoStats';
import { useDragScroll } from '@/hooks/useDragScroll';
import { useUiStore } from '@/store/uiStore';

/** Super-like count ranges for the rating filter. */
const RATING_RANGES: Record<string, [number, number]> = {
  '0-1k': [0, 1_000],
  '1k-10k': [1_000, 10_000],
  '10k-100k': [10_000, 100_000],
  '100k+': [100_000, Infinity],
};

const QUICK_DOMAINS: string[] = [
  'Science',
  'Entertainment',
  'Philosophy',
  'Politics',
  'Sports',
  'Business',
  'Exploration',
];

/** Minimum width (px) for category pill badges; all pills match the widest. */
const MIN_PILL_WIDTH = 292;

/** Right-edge fade overlay matching the page background. */
const CAROUSEL_FADE_GRADIENT =
  'linear-gradient(93.12deg, rgba(26, 16, 54, 0) 10.65%, #1A1036 78.01%)';

const InlineFilterCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, checked, onChange }) => (
  <button
    onClick={onChange}
    className='group flex items-center gap-[9px] transition-opacity hover:opacity-80'
  >
    <div
      className={clsx(
        'flex size-5 items-center justify-center rounded border transition-colors',
        checked
          ? 'border-accent-300 bg-accent-300'
          : 'border-transparent bg-white group-hover:border-accent-200'
      )}
    >
      {checked && <CheckIcon className='size-3.5 stroke-[3] text-brand-800' />}
    </div>
    <span className='font-sans text-14 font-medium text-white'>{label}</span>
  </button>
);

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedEcho } = useChatContext();
  const openModal = useUiStore((state) => state.openModal);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
  const [activeFilters, setActiveFilters] = React.useState<ActiveFilters>({
    category: [],
    rating: [],
    popularity: [],
  });

  const { publicEchoesList, myEchoes, isLoading: isStoreLoading } = useEchoStore();

  const echoIds = React.useMemo(() => publicEchoesList.map((e) => e.id), [publicEchoesList]);
  const { statsMap, popularityMap, statsByEchoId } = useAllEchoStats(echoIds);

  const activePopularity = activeFilters.popularity[0] as PopularityFilter | undefined;
  const { data: trendingIds = [] } = useTrendingEchoIds(true);

  const echoRecord = React.useMemo(() => {
    const rec: Record<string, Echo> = {};
    for (const e of publicEchoesList) rec[e.id] = e;
    return rec;
  }, [publicEchoesList]);
  const { idToSlug } = React.useMemo(() => buildEchoSlugMap(echoRecord), [echoRecord]);

  const hasCreatedEcho = myEchoes.length > 0;
  const myEchoCount = myEchoes.length;

  const firstEcho = myEchoes[0];
  const displayEcho = firstEcho
    ? {
        id: firstEcho.id,
        name: firstEcho.name ?? 'My Echo',
        category: firstEcho.category ?? 'Personal',
        domain: firstEcho.domain ?? 'General',
        imageUrl: firstEcho.profile_image_url ?? null,
      }
    : null;

  const featuredDragRef = useDragScroll();

  const mobileFilterRef = React.useRef<HTMLDivElement>(null);
  const desktopFilterRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    analytics.homePageViewed();
  }, []);

  const handleToggleFilter = (section: FilterSectionId, id: string) => {
    setActiveFilters((prev) => {
      const sectionFilters = prev[section];
      const isSelected = sectionFilters.includes(id);

      const nextFilters =
        section === 'popularity'
          ? isSelected
            ? []
            : [id]
          : isSelected
            ? sectionFilters.filter((i) => i !== id)
            : [...sectionFilters, id];

      if (!isSelected) {
        analytics.echoFilterApplied(section, id, 0);
      }

      return {
        ...prev,
        [section]: nextFilters,
      };
    });
  };

  const handleClearFilters = () => {
    setActiveFilters({ category: [], rating: [], popularity: [] });
  };

  const handleEchoClick = (id: string) => {
    const echoName = publicEchoesList.find((e) => e.id === id)?.name || 'Unknown';
    analytics.echoSelected(id, echoName, 'home');

    setSelectedEcho(id as EchoKey);
    const slug = idToSlug[id] ?? id;
    navigate(`/messages/${slug}`, { state: { openChat: true } });
  };

  const handleMyEchoAction = () => {
    analytics.createEchoCTAClicked('home');
    if (hasCreatedEcho) {
      navigate('/my-echo');
    } else {
      openModal('creator');
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideMobile = !!(mobileFilterRef.current && mobileFilterRef.current.contains(target));
      const insideDesktop = !!(
        desktopFilterRef.current && desktopFilterRef.current.contains(target)
      );
      if (!insideMobile && !insideDesktop) {
        setIsFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dynamicFilterSections = React.useMemo(() => {
    const categoryOptions = getUniqueDomains(publicEchoesList);
    return FILTER_SECTIONS.map((section) => {
      if (section.id === 'category') {
        return {
          ...section,
          options: categoryOptions.length > 0 ? categoryOptions : section.options,
        };
      }
      return section;
    });
  }, [publicEchoesList]);

  /** Featured echoes: unaffected by category/search filters. */
  const featuredEchoes = React.useMemo(() => publicEchoesList.slice(0, 6), [publicEchoesList]);

  /** All Echoes filtered by search + category + rating + popularity. */
  const filteredEchoes = React.useMemo(() => {
    const base = publicEchoesList.filter((echo) => {
      if (searchQuery) {
        const matchesName = echo.name.toLowerCase().includes(searchQuery.toLowerCase());
        if (!matchesName) return false;
      }

      if (activeFilters.category.length > 0) {
        const mappedDomain = getEchoDomain(echo);
        if (!mappedDomain) return false;
        if (!activeFilters.category.includes(mappedDomain)) return false;
      }

      if (activeFilters.rating.length > 0) {
        const stars = statsMap.get(echo.id) ?? 0;
        const matchesAnyRange = activeFilters.rating.some((filterId) => {
          const range = RATING_RANGES[filterId];
          if (!range) return false;
          const [min, max] = range;
          return stars >= min && stars < max;
        });
        if (!matchesAnyRange) return false;
      }

      return true;
    });

    if (activePopularity) {
      return sortEchoesByPopularity(base, activePopularity, popularityMap, trendingIds);
    }
    return base;
  }, [
    publicEchoesList,
    searchQuery,
    activeFilters,
    statsMap,
    activePopularity,
    popularityMap,
    trendingIds,
  ]);

  /** Group filtered echoes by domain, preserving domain order. */
  const groupedEchoes = React.useMemo(() => {
    const groups = new Map<string, Echo[]>();
    for (const echo of filteredEchoes) {
      const domain = getEchoDomain(echo);
      const list = groups.get(domain);
      if (list) {
        list.push(echo);
      } else {
        groups.set(domain, [echo]);
      }
    }
    return groups;
  }, [filteredEchoes]);

  React.useEffect(() => {
    if (!searchQuery) return;
    const handle = setTimeout(() => {
      analytics.echoSearched(searchQuery, filteredEchoes.length);
    }, 500);
    return () => clearTimeout(handle);
  }, [searchQuery, filteredEchoes.length]);

  const SectionHeader = ({
    title,
    subtitle,
    className,
  }: {
    title: string;
    subtitle?: string;
    className?: string;
  }) => (
    <div className={clsx('flex flex-col gap-2', className)}>
      <h2 className='font-sans text-16 font-semibold leading-nav text-white'>{title}</h2>
      {subtitle && (
        <div className='hidden h-[40px] items-center md:flex'>
          <p
            className={clsx(
              'font-sans font-medium',
              'text-[16px] leading-105 tracking-normal',
              'text-gray-325'
            )}
          >
            {subtitle}
          </p>
        </div>
      )}
    </div>
  );

  const pillWidthsRef = React.useRef(new Map<string, number>());
  const [maxPillWidth, setMaxPillWidth] = React.useState(MIN_PILL_WIDTH);

  const measurePill = React.useCallback((category: string, el: HTMLDivElement | null) => {
    if (!el) return;
    const width = el.scrollWidth;
    const prev = pillWidthsRef.current.get(category);
    if (prev === width) return;
    pillWidthsRef.current.set(category, width);
    const max = Math.max(MIN_PILL_WIDTH, ...pillWidthsRef.current.values());
    setMaxPillWidth(max);
  }, []);

  const CategoryCarousel: React.FC<{ category: string; echoes: Echo[] }> = ({
    category,
    echoes,
  }) => {
    const dragRef = useDragScroll();
    return (
      <div className='flex flex-col gap-4'>
        <div
          ref={(el) => measurePill(category, el)}
          className='flex h-[42px] items-center rounded-[22px] bg-accent-100/40 px-5'
          style={{ width: maxPillWidth }}
        >
          <h3 className='whitespace-nowrap font-sans text-[22px] font-bold leading-27 text-white'>
            {category}
          </h3>
        </div>

        <div className='relative w-full'>
          <div
            ref={dragRef}
            className='custom-scrollbar flex cursor-grab gap-6 overflow-x-auto pb-4 pr-6 md:pr-24'
          >
            {echoes.map((echo) => (
              <div key={`cat-${echo.id}`} className='w-[209px] shrink-0 md:w-[240px] xl:w-[563px]'>
                <SmartEchoCard
                  echo={echo}
                  imageSrc={echo.imageUrl || noEchoImage}
                  borderColor='#BFB0F8'
                  onClick={() => handleEchoClick(echo.id)}
                  onAction={() => handleEchoClick(echo.id)}
                  className='!h-[209px] !w-full !rounded-[30px] !border-4 md:!h-[240px] xl:!h-[320px]'
                  badgeContainerClassName='gap-2'
                  starBadgeClassName='!bg-accent-610 !rounded-[20px] !px-3'
                  imageContainerClassName='!left-0 !translate-x-0 !w-full !h-full !justify-end'
                  imageClassName='!w-auto !h-full object-cover'
                  glowClassName='!left-auto !right-[-20%] top-[70%] !translate-x-0 w-[250%]'
                  hidePanel={true}
                  showTextOverlay={true}
                />
              </div>
            ))}
          </div>

          <div
            className='pointer-events-none absolute right-0 top-0 z-20 h-full w-24'
            style={{
              background: CAROUSEL_FADE_GRADIENT,
            }}
            aria-hidden='true'
          />
        </div>
      </div>
    );
  };

  const renderMyEchoCard = () => {
    if (hasCreatedEcho && displayEcho) {
      return (
        <SmartEchoCard
          key='my-echo-created'
          echo={displayEcho}
          imageSrc={displayEcho.imageUrl ?? echoCreatedImage}
          borderColor='#A4FF66'
          onClick={handleMyEchoAction}
          className='!h-[209px] !w-full !max-w-[163px] !rounded-20 !border-4 shadow-lg transition-transform md:!h-[240px] md:!max-w-none xl:!h-[320px] xl:!max-w-none'
          imageContainerClassName='!left-0 !translate-x-0 !w-full !h-[85%] !justify-end xl:!h-full'
          badgeContainerClassName='xl:!justify-between'
          fansBadgeClassName='!text-[10px] xl:!text-12'
          starBadgeClassName='!text-[10px] xl:!text-12 !justify-end'
        />
      );
    }

    return (
      <EchoCard
        key='my-echo-create-prompt'
        variant='create'
        imageSrc={noEchoImage}
        onAction={handleMyEchoAction}
        echoCount={myEchoCount}
        actionLabel='CREATE ECHO'
        onClick={handleMyEchoAction}
        className='!h-[209px] !w-full !max-w-[163px] !rounded-20 shadow-lg transition-transform md:!h-[240px] md:!max-w-none xl:!h-[320px] xl:!max-w-none'
        imageContainerClassName='!h-[85%] xl:!h-full'
        badgeContainerClassName='xl:!justify-between'
        fansBadgeClassName='!text-[10px] xl:!text-12'
        starBadgeClassName='!text-[10px] xl:!text-12 !justify-end'
      />
    );
  };

  const contentPadding = 'px-6 md:px-8';

  return (
    <BatchedEchoStatsProvider statsByEchoId={statsByEchoId}>
      <div className='relative size-full overflow-hidden bg-brand-800'>
        <img
          src={waveContactListSvg}
          alt=''
          className='pointer-events-none absolute bottom-0 left-0 z-0 select-none'
        />
        <div className='custom-scrollbar relative z-10 flex size-full flex-col overflow-y-auto py-6 md:py-10'>
          <div className={contentPadding}>
            <PageHeader title='Discover Echoes' titleClassName='text-gray-100' theme='light'>
              <HeaderCredits />
            </PageHeader>

            <p
              className={clsx(
                'md:hidden',
                'mb-6',
                'font-sans font-medium',
                'text-[16px] leading-105 tracking-normal',
                'text-gray-325'
              )}
            >
              Connect with your favorite celebrities
            </p>

            <div className='mb-10 w-full shrink-0'>
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder='Search a chat'
                className='w-full border-transparent bg-white md:w-[618px]'
              />
            </div>
          </div>

          <div className='mb-12 shrink-0'>
            <div className='hidden items-start xl:flex xl:flex-row'>
              <div className={clsx('flex w-[360px] shrink-0 flex-col gap-4', 'pl-6 md:pl-10')}>
                <SectionHeader title='My Echo' subtitle='Talk and share your echo with anyone' />
                {renderMyEchoCard()}
              </div>

              <div className='flex min-w-0 flex-1 flex-col gap-4 pl-[2.9375rem]'>
                <SectionHeader
                  title='Featured Echoes'
                  subtitle='Top celebrities to start valuable conversations'
                />

                <div className='relative w-full'>
                  <div
                    ref={featuredDragRef}
                    className='custom-scrollbar flex cursor-grab gap-6 overflow-x-auto pb-4 pr-6 md:pr-24'
                  >
                    {featuredEchoes.map((echo) => (
                      <div key={`featured-d-${echo.id}`} className='w-full shrink-0 xl:w-[563px]'>
                        <SmartEchoCard
                          echo={echo}
                          imageSrc={echo.imageUrl || noEchoImage}
                          borderColor='#BFB0F8'
                          onClick={() => handleEchoClick(echo.id)}
                          onAction={() => handleEchoClick(echo.id)}
                          className='!h-[320px] !w-full !rounded-[30px] !border-4'
                          badgeContainerClassName='gap-2'
                          starBadgeClassName='!bg-accent-610 !rounded-[20px] !px-3'
                          imageContainerClassName='!left-0 !translate-x-0 !w-full !h-full !justify-end'
                          imageClassName='!w-auto !h-full object-cover'
                          hidePanel={true}
                          showTextOverlay={true}
                        />
                      </div>
                    ))}
                    {isStoreLoading && featuredEchoes.length === 0 && (
                      <div className='text-white'>Loading echoes...</div>
                    )}
                  </div>

                  <div
                    className='pointer-events-none absolute right-0 top-0 z-20 h-full w-24'
                    style={{
                      background: CAROUSEL_FADE_GRADIENT,
                    }}
                    aria-hidden='true'
                  />
                </div>
              </div>
            </div>

            <div className={clsx('flex flex-col gap-6 xl:hidden', contentPadding)}>
              <div className='flex flex-col gap-6 md:flex-row md:flex-wrap'>
                <div className='flex flex-col gap-4 md:w-[calc(50%-0.75rem)]'>
                  <SectionHeader title='My Echo' />
                  <div className='w-full max-w-[163px] md:max-w-none'>{renderMyEchoCard()}</div>
                </div>

                <div className='flex flex-col gap-4 md:w-[calc(50%-0.75rem)]'>
                  <SectionHeader title='Featured Echoes' />
                  {(() => {
                    const firstFeatured = featuredEchoes[0];
                    if (!firstFeatured) return null;
                    return (
                      <SmartEchoCard
                        key={`featured-m-0`}
                        echo={firstFeatured}
                        imageSrc={firstFeatured.imageUrl || noEchoImage}
                        borderColor='#BFB0F8'
                        onAction={() => handleEchoClick(firstFeatured.id)}
                        onClick={() => handleEchoClick(firstFeatured.id)}
                        className='!h-auto w-full !rounded-20'
                        badgeContainerClassName='gap-2'
                        starBadgeClassName='!bg-accent-610 !rounded-[20px] !px-3'
                        imageContainerClassName='!h-[85%]'
                        hidePanel={true}
                        showTextOverlay={true}
                      />
                    );
                  })()}
                </div>

                {featuredEchoes.slice(1, 4).map((echo) => (
                  <div
                    key={`featured-m-${echo.id}`}
                    className='flex flex-col justify-end md:w-[calc(50%-0.75rem)]'
                  >
                    <SmartEchoCard
                      echo={echo}
                      imageSrc={echo.imageUrl || noEchoImage}
                      borderColor='#BFB0F8'
                      onAction={() => handleEchoClick(echo.id)}
                      onClick={() => handleEchoClick(echo.id)}
                      className='!h-auto w-full !rounded-20'
                      badgeContainerClassName='gap-2'
                      starBadgeClassName='!bg-accent-610 !rounded-[20px] !px-3'
                      imageContainerClassName='!h-[85%]'
                      hidePanel={true}
                      showTextOverlay={true}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={clsx('flex-1', contentPadding)}>
            <div className='mb-6 flex items-center gap-6 xl:items-start'>
              <SectionHeader
                title='All Echoes'
                subtitle='Speak to your favorite person'
                className='shrink-0'
              />

              <div className='relative ml-auto xl:hidden' ref={mobileFilterRef}>
                <button
                  onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                  className='flex items-center justify-center gap-2 rounded-30 transition-transform hover:opacity-90 active:scale-95'
                  style={{
                    width: '94px',
                    height: '40px',
                    background: `linear-gradient(#1A1036, #1A1036) padding-box, linear-gradient(180deg, #ABF56E 0%, #BFB0F8 100%) border-box`,
                    border: '1px solid transparent',
                  }}
                >
                  <img src={filterIcon} alt='' className='size-4' />
                  <span className='font-sans text-16 font-medium leading-nav text-white'>
                    Filters
                  </span>
                </button>

                <FilterPopup
                  isOpen={isFiltersOpen}
                  onClose={() => setIsFiltersOpen(false)}
                  activeFilters={activeFilters}
                  onToggleFilter={handleToggleFilter}
                  onClearAll={handleClearFilters}
                  className='right-0 top-full mt-2 w-[300px] md:left-auto md:right-0 md:w-filter-popup'
                  sections={dynamicFilterSections}
                />
              </div>

              <div className='hidden w-full flex-col gap-2 xl:flex'>
                <SectionHeader title='Category' />

                <div className='flex w-full flex-wrap items-center gap-[22px]'>
                  {QUICK_DOMAINS.map((domain) => (
                    <InlineFilterCheckbox
                      key={domain}
                      label={domain}
                      checked={activeFilters.category.includes(domain)}
                      onChange={() => handleToggleFilter('category', domain)}
                    />
                  ))}

                  <div className='relative ml-auto' ref={desktopFilterRef}>
                    <button
                      onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                      className='flex items-center justify-center gap-2 rounded-30 px-4 py-3 transition-transform hover:opacity-90 active:scale-95'
                      style={{
                        background: `linear-gradient(#1A1036, #1A1036) padding-box, linear-gradient(180deg, #ABF56E 0%, #BFB0F8 100%) border-box`,
                        border: '1px solid transparent',
                      }}
                    >
                      <img src={filterIcon} alt='' className='size-5' />
                      <span className='whitespace-nowrap font-sans text-14 font-medium leading-nav text-white'>
                        More Filters
                      </span>
                    </button>

                    <FilterPopup
                      isOpen={isFiltersOpen}
                      onClose={() => setIsFiltersOpen(false)}
                      activeFilters={activeFilters}
                      onToggleFilter={handleToggleFilter}
                      onClearAll={handleClearFilters}
                      className='right-0 top-full mt-2 w-[300px] md:w-filter-popup'
                      sections={dynamicFilterSections}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='flex flex-col gap-12'>
              {activePopularity ? (
                <CategoryCarousel
                  category={POPULARITY_LABELS[activePopularity]}
                  echoes={filteredEchoes}
                />
              ) : (
                [...groupedEchoes.entries()].map(([category, echoes]) => (
                  <CategoryCarousel key={category} category={category} echoes={echoes} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </BatchedEchoStatsProvider>
  );
};

export default HomePage;
